/**
 * Patient medication reconciliation after a SecureLink Discharge Import sync.
 * Not ChartMind-I/I2 clinician med-rec dialogs. Resolutions persist on the
 * pending-med-rec document so a later Sync does not wipe in-progress decisions.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';
import { contentCardSx } from 'styles/standaloneLayoutTokens';
import { masterChartPath } from 'constants/masterChartRoutes';
import { resolveMedReconciliationItem } from 'services/cmr/ehrDischargeService';

function isResolved(item) {
  return Boolean(item?.resolution);
}

function sourceLineAdd(item, institutionName) {
  if (item?.clinicianName) {
    return `Prescribed by ${item.clinicianName} at ${institutionName}`;
  }
  return `From ${institutionName}`;
}

function sourceLineRemove(item, institutionName) {
  if (item?.clinicianName) {
    return `${item.clinicianName} at ${institutionName} advises stopping this`;
  }
  return `From ${institutionName}: advised to stop this`;
}

function medDetail(item) {
  return [item.dose, item.route, item.frequency].filter(Boolean).join(' · ');
}

const MedReconciliationPage = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [doc, setDoc] = useState(undefined);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyItem, setBusyItem] = useState(null);
  const [finishingLater, setFinishingLater] = useState(false);

  useEffect(() => {
    if (!user?.uid || !linkId) return undefined;
    const ref = db
      .collection('pendingMedReconciliations')
      .doc(user.uid)
      .collection('items')
      .doc(linkId);
    const unsub = ref.onSnapshot(
      (snap) => {
        setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null);
        setLoadError(null);
      },
      () => {
        setLoadError('Unable to load medication review.');
        setDoc(null);
      },
    );
    return () => unsub();
  }, [user?.uid, linkId]);

  const institutionName = doc?.institutionName || 'your care facility';
  const toAdd = useMemo(() => doc?.toAdd || [], [doc]);
  const toRemove = useMemo(() => doc?.toRemove || [], [doc]);
  const total = toAdd.length + toRemove.length;
  const resolvedCount = [...toAdd, ...toRemove].filter(isResolved).length;
  const completed = doc?.status === 'completed' || (total > 0 && resolvedCount === total);

  const decide = useCallback(
    async (itemId, decision) => {
      setActionError(null);
      setBusyItem(itemId);
      try {
        await resolveMedReconciliationItem({ linkId, itemId, decision });
      } catch {
        setActionError('That update did not save. Please try again.');
      } finally {
        setBusyItem(null);
      }
    },
    [linkId],
  );

  const finishLater = useCallback(async () => {
    setFinishingLater(true);
    setActionError(null);
    try {
      await resolveMedReconciliationItem({ linkId, decision: 'finishLater' });
      navigate(masterChartPath());
    } catch {
      setActionError('Could not save progress. Please try again.');
    } finally {
      setFinishingLater(false);
    }
  }, [linkId, navigate]);

  if (doc === undefined && !loadError) {
    return (
      <div className="inner">
        <Box sx={{ py: 4 }}>
          <LinearProgress />
        </Box>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="inner">
        <DashboardPageHeader title="Medication Review" sx={{ mt: { xs: 1, sm: 5 } }} />
        <DataLoadErrorAlert message={loadError} />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="inner">
        <DashboardPageHeader title="Medication Review" sx={{ mt: { xs: 1, sm: 5 } }} />
        <Alert severity="info" sx={{ mb: 2 }}>
          No medication review is waiting for this link. It may already be
          complete, or records have not been imported yet.
        </Alert>
        <Button onClick={() => navigate(masterChartPath())}>
          Go to MasterChart
        </Button>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="inner">
        <DashboardPageHeader
          title="Medication Review"
          subtitle="All medication changes have been reviewed."
          sx={{ mt: { xs: 1, sm: 5 } }}
        />
        <Alert severity="success" sx={{ mb: 2 }}>
          All done. Your medication list is up to date.
        </Alert>
        <Button onClick={() => navigate('/dashboard/medications')}>
          Go to medications
        </Button>
      </div>
    );
  }

  const openAdd = toAdd.filter((i) => !isResolved(i));
  const openRemove = toRemove.filter((i) => !isResolved(i));

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Medication Review"
        subtitle={institutionName}
        sx={{ mt: { xs: 1, sm: 5 } }}
        actions={
          <LoadingButton onClick={finishLater} loading={finishingLater}>
            Finish later
          </LoadingButton>
        }
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {resolvedCount} of {total} reviewed
      </Typography>
      <LinearProgress
        variant="determinate"
        value={total ? (resolvedCount / total) * 100 : 0}
        sx={{ mb: 3 }}
      />

      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        New medications
      </Typography>
      {openAdd.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No new medications left to review.
        </Typography>
      ) : (
        openAdd.map((item) => (
          <Paper key={item.itemId} elevation={0} sx={{ ...contentCardSx, p: 2, mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {item.name}
            </Typography>
            {medDetail(item) ? (
              <Typography variant="body2" color="text.secondary">
                {medDetail(item)}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {sourceLineAdd(item, institutionName)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <LoadingButton
                variant="contained"
                loading={busyItem === item.itemId}
                disabled={Boolean(busyItem)}
                onClick={() => decide(item.itemId, 'added')}
              >
                Add to my list
              </LoadingButton>
              <Button
                disabled={Boolean(busyItem)}
                onClick={() => decide(item.itemId, 'skipped')}
              >
                Skip for now
              </Button>
            </Box>
          </Paper>
        ))
      )}

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Medications to stop
      </Typography>
      {openRemove.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No medications left to review for removal.
        </Typography>
      ) : (
        openRemove.map((item) => (
          <Paper key={item.itemId} elevation={0} sx={{ ...contentCardSx, p: 2, mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {item.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              {sourceLineRemove(item, institutionName)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <LoadingButton
                color="error"
                variant="contained"
                loading={busyItem === item.itemId}
                disabled={Boolean(busyItem)}
                onClick={() => decide(item.itemId, 'removed')}
              >
                Remove from my list
              </LoadingButton>
              <Button
                disabled={Boolean(busyItem)}
                onClick={() => decide(item.itemId, 'kept')}
              >
                Keep on my list
              </Button>
            </Box>
          </Paper>
        ))
      )}
    </div>
  );
};

export default MedReconciliationPage;
